// Keep Rubik's combined-listing selection aligned with Horizon's native card.
// Only react to an actual card swatch click; ordinary product/variant pickers stay native.
const selections = new WeakMap();
const products = new Map();
const normalize = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const words = (value) => String(value || '').toLowerCase().replace(/\band\b/g, ' ').match(/[a-z0-9]+/g) || [];
const includesOption = (label, option) => {
  const selected = words(label);
  const required = words(option);
  return required.length > 0 && selected.some((_, start) => required.every((word, index) => selected[start + index] === word));
};

export function resolveCardVariant(product, label, requestedId) {
  const variants = product.variants || [];
  if (requestedId) return variants.find((variant) => String(variant.id) === String(requestedId)) || null;
  if (variants.length === 1) return variants[0];
  const candidates = variants.filter((variant) => {
    // Single-valued options (e.g. color count) need no separate customer choice.
    const options = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
    return options.every((option, index) => {
      const values = new Set(variants.map((item) => normalize((item.options || [item.option1, item.option2, item.option3])[index])));
      return values.size === 1 || includesOption(label, option);
    });
  });
  return candidates.length === 1 ? candidates[0] : null;
}

function productUrl(href) {
  const url = new URL(href, location.href);
  if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) return null;
  return url;
}

async function loadProduct(url) {
  const jsonUrl = new URL(url);
  jsonUrl.pathname = jsonUrl.pathname.replace(/\/$/, '') + '.js';
  jsonUrl.search = '';
  const key = jsonUrl.href;
  if (!products.has(key)) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    const request = fetch(key, { credentials: 'same-origin', signal: controller.signal }).then(async (response) => {
      if (!response.ok) throw new Error('Product unavailable');
      return response.json();
    }).catch((error) => {
      products.delete(key);
      throw error;
    }).finally(() => clearTimeout(timeout));
    products.set(key, request);
  }
  return products.get(key);
}

function setLinks(root, url, title) {
  root.querySelectorAll('a[href]').forEach((link) => {
    if (!productUrl(link.href)) return;
    link.href = url;
    if (link.hasAttribute('aria-label')) link.setAttribute('aria-label', title);
  });
}

function setTitle(card, title) {
  card.querySelectorAll('[ref="productCardLink"] .visually-hidden, [ref="productTitleLink"] [role="heading"], .product-grid-view-zoom-out--details h3').forEach((element) => { element.textContent = title; });
}

function setQuickAdd(card, product, variant, url) {
  card.querySelectorAll('quick-add-component').forEach((quickAdd) => {
    quickAdd.dataset.productTitle = product.title;
    quickAdd.dataset.productOptionsCount = String(product.options?.length || 1);
    quickAdd.dataset.quickAddButton = variant ? 'add' : 'choose';
    quickAdd.querySelectorAll('product-form-component').forEach((form) => {
      form.dataset.productId = String(product.id);
      const input = form.querySelector('input[name="id"]');
      if (input) {
        input.value = variant ? String(variant.id) : '';
        input.setAttribute('value', input.value);
        input.disabled = !variant?.available;
      }
      const quantity = form.querySelector('input[name="quantity"]');
      if (quantity) quantity.value = String(variant?.quantity_rule?.min || 1);
      const add = form.querySelector('.quick-add__button--add');
      if (add) add.disabled = !variant?.available;
      const choose = form.querySelector('.quick-add__button--choose');
      if (choose) choose.disabled = false;
    });
    // Native quick-add reads this card's URL, including its explicit variant.
    setLinks(quickAdd, url, product.title);
  });
}

function replaceBlock(card, template, selector) {
  const existing = card.querySelector(selector);
  const replacement = template?.content.querySelector(selector)?.cloneNode(true);
  if (existing) {
    if (replacement) existing.replaceWith(replacement);
    else existing.remove();
  } else if (replacement) {
    const price = card.querySelector('product-price');
    price?.after(replacement);
  }
}

function updateMedia(root, source, product, variant, url) {
  root.querySelectorAll('[ref="cardGallery"]').forEach((gallery) => { gallery.dataset.productId = String(product.id); });
  root.querySelectorAll('add-to-cart-component').forEach((button) => { button.dataset.productVariantMedia = source.dataset.featuredMediaUrl || ''; });
  setLinks(root, url, product.title);
  // Inactive desktop/mobile gallery copies also contain native quick-add forms.
  setQuickAdd(root, product, variant, url);
  const slides = source.querySelector('template[data-card-media]');
  if (!slides?.content.firstElementChild) {
    root.querySelectorAll('.product-media, .result-card-primary__image').forEach((media) => { media.hidden = true; });
    return;
  }
  const originalImage = root.querySelector('img.product-media__image, img.result-card-primary__image');
  const sizes = originalImage?.getAttribute('sizes');
  root.querySelectorAll('slideshow-slides').forEach((scroller) => {
    const media = slides.content.cloneNode(true);
    media.querySelectorAll('img').forEach((image) => {
      if (sizes) image.setAttribute('sizes', sizes);
      image.dataset.rclOriginalSrc = image.getAttribute('src') || '';
      image.dataset.rclOriginalSrcset = image.getAttribute('srcset') || '';
    });
    scroller.replaceChildren(media);
    const slideshow = scroller.closest('slideshow-component');
    if (slideshow) {
      slideshow.setAttribute('initial-slide', '0');
      slideshow.dataset.genericMediaSize = String(scroller.children.length);
      slideshow.style.setProperty('--slideshow-timeline', [...scroller.children].map((_, index) => `--slide-${index}`).join(', '));
      if (slideshow.isConnected) requestAnimationFrame(() => {
        if (typeof slideshow.select === 'function') Promise.resolve(slideshow.select(0, undefined, { animate: false })).catch(() => {});
      });
    }
  });
  const primary = root.querySelector('img.result-card-primary__image');
  const firstImage = slides.content.querySelector('img');
  if (primary && firstImage) {
    primary.hidden = false;
    for (const attribute of ['src', 'srcset', 'alt', 'width', 'height', 'data_max_resolution']) {
      if (firstImage.hasAttribute(attribute)) primary.setAttribute(attribute, firstImage.getAttribute(attribute));
      else primary.removeAttribute(attribute);
    }
    primary.dataset.rclOriginalSrc = primary.getAttribute('src') || '';
    primary.dataset.rclOriginalSrcset = primary.getAttribute('srcset') || '';
  }
}

async function syncSelection(card, swatch) {
  const url = productUrl(swatch.href);
  if (!url) return;
  const previous = selections.get(card);
  previous?.controller.abort();
  const state = { pending: true, controller: new AbortController(), url: url.href };
  selections.set(card, state);
  card.setAttribute('aria-busy', 'true');
  card.querySelectorAll('quick-add-component button').forEach((button) => { button.disabled = true; });
  card.querySelectorAll('.product-card-rating, .product-card-comparison').forEach((block) => { block.hidden = true; });
  state.promise = (async () => {
    let product;
    let variant;
    try {
      product = await loadProduct(url);
      if (selections.get(card) !== state || !card.isConnected) return;
      variant = resolveCardVariant(product, swatch.dataset.optionValue || swatch.getAttribute('aria-label'), url.searchParams.get('variant'));
      if (variant) url.searchParams.set('variant', String(variant.id));
      else url.searchParams.delete('variant');
      state.url = url.href;
      setLinks(card, url.href, product.title);
      card.dataset.productId = String(product.id);
      setTitle(card, product.title);
      const sectionUrl = new URL(url);
      sectionUrl.searchParams.set('section_id', 'caliart-card-selection');
      state.timeout = setTimeout(() => state.controller.abort(), 8000);
      const response = await fetch(sectionUrl, { credentials: 'same-origin', signal: state.controller.signal });
      if (!response.ok) throw new Error('Card unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = html.querySelector('[data-caliart-card-selection]');
      if (!source || source.dataset.productId !== String(product.id)) throw new Error('Card mismatch');
      if (variant && source.dataset.variantId !== String(variant.id)) throw new Error('Variant mismatch');
      if (selections.get(card) !== state || !card.isConnected) return;
      const newPrice = source.querySelector('template[data-card-price]')?.content.querySelector('[ref="priceContainer"]');
      if (newPrice) card.querySelectorAll('product-price [ref="priceContainer"]').forEach((price) => { price.replaceWith(newPrice.cloneNode(true)); });
      card.querySelectorAll('product-price').forEach((price) => { price.dataset.productId = String(product.id); });
      replaceBlock(card, source.querySelector('template[data-card-comparison]'), '.product-card-comparison');
      replaceBlock(card, source.querySelector('template[data-card-rating]'), '.product-card-rating');
      card.dataset.featuredMediaUrl = source.dataset.featuredMediaUrl || '';
      const resultMedia = card.querySelector('result-card-media');
      if (typeof resultMedia?.updateMedia === 'function') {
        resultMedia.updateMedia((root) => updateMedia(root, source, product, variant, url.href));
      } else updateMedia(card, source, product, variant, url.href);
      setQuickAdd(card, product, variant, url.href);
      // A specific style choice should be visible in the card summary as well.
      if (variant && product.variants.length > 1) {
        const comparison = card.querySelector('.product-card-comparison');
        if (comparison) {
          const item = document.createElement('li');
          item.textContent = source.dataset.variantTitle;
          comparison.lastElementChild?.replaceWith(item);
        }
      }
    } catch (error) {
      if (selections.get(card) !== state) return;
      // Preserve a correct detail URL when available; require selection in the
      // native modal instead of submitting an old variant after a failed fetch.
      const fallback = product || { id: '', title: swatch.querySelector('img')?.alt || swatch.getAttribute('aria-label') || '', options: [] };
      setLinks(card, state.url, fallback.title);
      setTitle(card, fallback.title);
      setQuickAdd(card, fallback, null, state.url);
      const resultMedia = card.querySelector('result-card-media');
      if (typeof resultMedia?.updateMedia === 'function') resultMedia.updateMedia((root) => {
        setLinks(root, state.url, fallback.title);
        setQuickAdd(root, fallback, null, state.url);
      });
    } finally {
      clearTimeout(state.timeout);
      if (selections.get(card) === state) {
        state.pending = false;
        card.removeAttribute('aria-busy');
      }
    }
  })();
}

window.addEventListener('click', (event) => {
  const path = event.composedPath();
  const host = path.find((node) => node instanceof Element && node.matches('rubik-combined-listings'));
  const swatch = path.find((node) => node instanceof HTMLAnchorElement && node.matches('.rcl__swatch'));
  const card = host?.closest('product-card');
  if (card && swatch && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
    // Run after Rubik's synchronous handler, preserving its widget and selection.
    queueMicrotask(() => syncSelection(card, swatch));
    return;
  }
  const clickedCard = event.target instanceof Element ? event.target.closest('product-card') : null;
  const state = clickedCard && selections.get(clickedCard);
  if (!state?.pending) return;
  const target = event.target.closest('a, button, input, label, select');
  if (target && !(target instanceof HTMLAnchorElement)) return;
  if (target instanceof HTMLAnchorElement && !productUrl(target.href)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const popup = event.ctrlKey || event.metaKey || event.shiftKey ? window.open('', '_blank') : null;
  if (popup) popup.opener = null;
  const navigate = async () => {
    let current = state;
    while (current?.pending) {
      const signal = current.controller.signal;
      if (signal.aborted) await current.promise;
      else {
        let interrupt;
        const aborted = new Promise((resolve) => { interrupt = resolve; signal.addEventListener('abort', interrupt, { once: true }); });
        try { await Promise.race([current.promise, aborted]); }
        finally { signal.removeEventListener('abort', interrupt); }
      }
      current = selections.get(clickedCard);
    }
    const href = current?.url || state.url;
    if (popup) popup.location.href = href;
    else location.assign(href);
  };
  navigate();
}, true);

window.addEventListener('submit', (event) => {
  const card = event.target instanceof Element ? event.target.closest('product-card') : null;
  if (card && selections.get(card)?.pending) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, true);
